import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  type Params,
} from '@angular/router';
import { filter, take } from 'rxjs/operators';

import { NotificationService } from '../notifications/notification.service';
import { canonicalizeClientCode } from '../session/client-code-canonicalizer';
import { isValidUtcDay } from '../time/utc-date-range';
import { FilterScopeController } from './filter-scope.service';
import {
  DATE_RANGE_PRESETS,
  DEFAULT_DATE_RANGE,
  DEFAULT_FILTER_STATE,
  FILTER_URL_PARAMS,
  FILTER_URL_UNSPECIFIED_STATE,
  FILTER_URL_STATUS_SEPARATOR,
  INVALID_FILTER_PARAMS_MESSAGE,
  STATUS_VALUES,
  type DateRangePreset,
  type DateRangeSelection,
  type FilterState,
  type StatusValue,
} from './filter-state.model';

/** The one preset that is never written to the URL (FR-011, FR-014). */
const DEFAULT_DATE_RANGE_PRESET =
  DEFAULT_DATE_RANGE.kind === 'preset' ? DEFAULT_DATE_RANGE.preset : null;

/** What one URL restore produced, and whether it needs the FR-013 notice. */
export interface FilterUrlRestore {
  readonly state: FilterState;
  /**
   * True when at least one **recognized** parameter was invalid and its
   * dimension fell back to the default. A client-role `client` parameter and an
   * out-of-list open-set value are deliberately NOT resets (they are a rule and
   * a feature respectively), and an unknown parameter is simply not ours.
   */
  readonly someWereReset: boolean;
}

/**
 * The URL as a **one-way projection** of the shared filter state (R-008).
 *
 * - **Read**, exactly once, at initialisation. This is the whole reason there
 *   is no oscillation: a naive bidirectional binding has the state write the
 *   URL, the router emit, the reader re-derive state, and republish. Making
 *   initialisation the only read direction removes the cycle without a
 *   suppression flag or an epoch counter.
 * - **Write**, on every published change, with `replaceUrl: true` so Back
 *   navigates between views and never through past filter states (FR-011), and
 *   `queryParamsHandling: 'merge'` with explicit `null`s so a cleared dimension
 *   deletes its own parameter while parameters this feature does not own
 *   survive untouched (FR-013).
 * - **Re-assert**, after any navigation whose resulting URL lost the current
 *   non-default parameters. Bare `routerLink`s (sidebar, breadcrumb) and
 *   programmatic `router.navigate` calls carry no query parameters, so without
 *   this every view change would leave a stale URL while the state — which
 *   survives navigation — still holds the filters. Centralising it here rather
 *   than decorating every link with `queryParamsHandling: 'preserve'` covers
 *   programmatic consumer navigations (AITH-45's drill-down) by construction.
 */
@Injectable()
export class FilterUrlProjection {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly filters = inject(FilterScopeController);
  private readonly notifications = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  private started = false;
  /**
   * Writing while the router is mid-navigation would cancel that navigation.
   * The re-assertion after `NavigationEnd` is the recovery path, so skipping is
   * safe — and it is why the initial restore's own publication needs no
   * suppression flag.
   */
  private navigating = false;

  initialize(): void {
    if (this.started) return;
    this.started = true;

    this.trackNavigationState();
    this.readOnce();
    this.writeOnPublish();
    this.reassertAfterNavigation();
  }

  // ─── Read: initialisation only (FR-012, R-008) ────────────────────────────

  private readOnce(): void {
    if (this.router.navigated) {
      // Already navigated (a lazily-created projection, or a test that
      // navigates first): the current URL is the first snapshot we get.
      this.applyFromUrl(this.router.url);
      return;
    }

    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      // NavigationStart rather than NavigationEnd: the destination's components
      // are created during the navigation, so restoring here means the first
      // subscribed view already sees the restored scope rather than fetching
      // once with defaults and again 300 ms later.
      .subscribe((event) => this.applyFromUrl(event.url));
  }

  private applyFromUrl(url: string): void {
    const restore = this.parse(this.router.parseUrl(url).queryParams);
    this.filters.applyRestoredState(restore.state);

    if (restore.someWereReset) {
      // Exactly one summarising toast per load, however many dimensions were
      // reset — one per parameter would bury a 20+-parameter page (SC-003).
      this.notifications.warning(INVALID_FILTER_PARAMS_MESSAGE);
    }
  }

  /** Parses the frozen URL surface into a state, per the filter-url validation table. */
  parse(queryParams: Params): FilterUrlRestore {
    let someWereReset = false;
    const markReset = (): void => {
      someWereReset = true;
    };

    // "Duplicated parameter → first occurrence wins", and a duplicate always
    // counts as a reset even though a usable value was taken from it.
    //
    // An empty value (`?provider=`) is what a stripped-out parameter looks
    // like, so it reads as ABSENT rather than as the empty string — otherwise a
    // dimension would hold `''`, which data-model §3 invariant 4 forbids.
    const read = (name: string): string | null => {
      const raw = queryParams[name];
      if (raw === undefined || raw === null) return null;
      if (Array.isArray(raw)) {
        markReset();
        return raw.length === 0 ? null : blankAsNull(String(raw[0]));
      }
      return blankAsNull(String(raw));
    };

    // GH-164 (aith-ui #47 review): App, Model and Provider VALUES are matched exactly by the API,
    // untrimmed (decisions D-03), so a link must restore them verbatim — ` Estimator ` selected in the
    // bar and written to the URL must not come back as `Estimator`. Only blankness is tested, and by
    // the published rule (C-1: empty or ASCII whitespace), not `trim()`, which also strips U+00A0 —
    // a real provider value on `!mp`.
    const readExact = (name: string): string | null => {
      const raw = queryParams[name];
      if (raw === undefined || raw === null) return null;
      if (Array.isArray(raw)) {
        markReset();
        return raw.length === 0 ? null : asciiBlankAsNull(String(raw[0]));
      }
      return asciiBlankAsNull(String(raw));
    };

    // GH-164 C-4 (review of aith-api #166 / aith-ui #45): a STATE parameter is a closed token, so it
    // is read exactly — untrimmed, and present-but-empty kept as `''` — and anything but
    // `unspecified` reaches the rejection below. Through `read`, ` unspecified ` was accepted and
    // `modelState=` vanished as absent, keeping `model=` with no notice.
    const readState = (name: string): string | null => {
      const raw = queryParams[name];
      if (raw === undefined || raw === null) return null;
      if (Array.isArray(raw)) {
        markReset();
        return raw.length === 0 ? null : String(raw[0]);
      }
      return String(raw);
    };

    return {
      state: {
        dateRange: this.parseDateRange(read, markReset),
        clientCode: this.parseClient(read, markReset),
        ...this.parseOpenSet('app', FILTER_URL_PARAMS.app, FILTER_URL_PARAMS.appState, readExact, readState, markReset),
        ...this.parseOpenSet('model', FILTER_URL_PARAMS.model, FILTER_URL_PARAMS.modelState, readExact, readState, markReset),
        ...this.parseOpenSet('provider', FILTER_URL_PARAMS.provider, FILTER_URL_PARAMS.providerState, readExact, readState, markReset),
        user: read(FILTER_URL_PARAMS.user) ?? null,
        status: this.parseStatus(read, markReset),
      },
      someWereReset,
    };
  }

  /**
   * GH-164 C-4: an open-set dimension is a value (`app=…`), the blank segment
   * (`appState=unspecified`), or neither. Both together, or a state other than
   * the exact lower-case `unspecified`, is invalid: both are dropped and the one
   * summarising notice is raised — the same treatment as every other invalid
   * parameter, so a link never silently means something its author did not write.
   */
  private parseOpenSet<D extends 'app' | 'model' | 'provider'>(
    dimension: D,
    valueName: string,
    stateName: string,
    readValue: (name: string) => string | null,
    readState: (name: string) => string | null,
    markReset: () => void,
  ): Record<D, string | null> & Record<`${D}Unspecified`, boolean> {
    const value = readValue(valueName);
    const state = readState(stateName);
    let selected: { value: string | null; unspecified: boolean } = { value, unspecified: false };
    if (state !== null) {
      if (state !== FILTER_URL_UNSPECIFIED_STATE || value !== null) {
        markReset();
        selected = { value: null, unspecified: false };
      } else {
        selected = { value: null, unspecified: true };
      }
    }
    return {
      [dimension]: selected.value,
      [`${dimension}Unspecified`]: selected.unspecified,
    } as Record<D, string | null> & Record<`${D}Unspecified`, boolean>;
  }

  private parseDateRange(
    read: (name: string) => string | null,
    markReset: () => void,
  ): DateRangeSelection {
    const presetToken = read(FILTER_URL_PARAMS.dateRange);
    const from = read(FILTER_URL_PARAMS.from);
    const to = read(FILTER_URL_PARAMS.to);

    if (presetToken !== null) {
      // "Both dateRange and from/to present → dateRange wins", and the
      // ambiguity itself counts as a reset.
      if (from !== null || to !== null) markReset();
      if ((DATE_RANGE_PRESETS as readonly string[]).includes(presetToken)) {
        return { kind: 'preset', preset: presetToken as DateRangePreset };
      }
      markReset();
      return DEFAULT_FILTER_STATE.dateRange;
    }

    if (from === null && to === null) return DEFAULT_FILTER_STATE.dateRange;

    // "Only one of from/to" is as invalid as a malformed or inverted pair.
    if (from === null || to === null || !isValidUtcDay(from) || !isValidUtcDay(to) || from > to) {
      markReset();
      return DEFAULT_FILTER_STATE.dateRange;
    }

    return { kind: 'custom', startUtcDay: from, endUtcDay: to };
  }

  private parseClient(
    read: (name: string) => string | null,
    markReset: () => void,
  ): string | null {
    const raw = read(FILTER_URL_PARAMS.client);
    if (raw === null) return null;

    // A `client` parameter for a client-portal role is ignored SILENTLY: it is
    // a rule (FR-005), not a user-facing error, so it raises no notice (SC-007).
    // Gated on HAVING a client role rather than on not-internal: the restore
    // runs before sign-in (memory-only OIDC storage), and a staff deep link's
    // client value must survive into the staff session. The controller's write
    // guard and effective-scope resolution enforce FR-005 regardless; dropping
    // it here keeps the toast honest.
    if (this.filters.isClientRole()) return null;

    const canonical = canonicalizeClientCode(raw);
    if (canonical === null) {
      markReset();
      return null;
    }
    return canonical;
  }

  private parseStatus(
    read: (name: string) => string | null,
    markReset: () => void,
  ): readonly StatusValue[] {
    const raw = read(FILTER_URL_PARAMS.status);
    if (raw === null) return DEFAULT_FILTER_STATE.status;

    const requested = raw.split(FILTER_URL_STATUS_SEPARATOR).map((value) => value.trim());
    const known = new Set(
      requested.filter((value): value is StatusValue =>
        (STATUS_VALUES as readonly string[]).includes(value),
      ),
    );

    // Unknown members are dropped and known ones kept; all-unknown collapses to
    // no constraint. Either way at least one member was invalid.
    if (known.size !== requested.length) markReset();

    return STATUS_VALUES.filter((value) => known.has(value));
  }

  // ─── Write: every published change (FR-011, R-008) ────────────────────────

  private writeOnPublish(): void {
    this.filters.scopeChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.writeCurrentParams());
  }

  private writeCurrentParams(): void {
    if (this.navigating) return;
    this.write(this.currentParams());
  }

  private write(queryParams: Params): void {
    void this.router.navigate([], {
      // The DEEPEST activated route, not the root: with empty commands the tree
      // resolves to that route's own URL, which is the current one. Relative to
      // the root it would resolve to `/` and navigate away from the page.
      relativeTo: this.deepestRoute(),
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** The frozen surface: only non-default selections appear (FR-011). */
  private currentParams(): Params {
    const state = this.filters.currentState();
    // Every parameter this feature owns is named explicitly, as a value or as
    // an explicit null — `merge` deletes on null, so a cleared dimension
    // removes its own parameter and leaves everyone else's alone.
    const params: Params = {
      [FILTER_URL_PARAMS.dateRange]: null,
      [FILTER_URL_PARAMS.from]: null,
      [FILTER_URL_PARAMS.to]: null,
      [FILTER_URL_PARAMS.client]: null,
      [FILTER_URL_PARAMS.app]: null,
      [FILTER_URL_PARAMS.model]: null,
      [FILTER_URL_PARAMS.provider]: null,
      [FILTER_URL_PARAMS.appState]: null,
      [FILTER_URL_PARAMS.modelState]: null,
      [FILTER_URL_PARAMS.providerState]: null,
      [FILTER_URL_PARAMS.user]: null,
      [FILTER_URL_PARAMS.status]: null,
    };

    if (state.dateRange.kind === 'preset') {
      // Only NON-default selections appear, so the default state and a fresh
      // load produce byte-identical URLs (filter-url contract, obligation 1).
      if (state.dateRange.preset !== DEFAULT_DATE_RANGE_PRESET) {
        params[FILTER_URL_PARAMS.dateRange] = state.dateRange.preset;
      }
    } else {
      // `to` is the INCLUSIVE last UTC day — human-facing, matching what the bar
      // displays; the half-open `toUtc` is derived internally (R-010).
      params[FILTER_URL_PARAMS.from] = state.dateRange.startUtcDay;
      params[FILTER_URL_PARAMS.to] = state.dateRange.endUtcDay;
    }

    if (this.filters.isInternalRole() && state.clientCode !== null) {
      params[FILTER_URL_PARAMS.client] = state.clientCode;
    }
    if (state.app !== null) params[FILTER_URL_PARAMS.app] = state.app;
    if (state.model !== null) params[FILTER_URL_PARAMS.model] = state.model;
    if (state.provider !== null) params[FILTER_URL_PARAMS.provider] = state.provider;
    if (state.appUnspecified) params[FILTER_URL_PARAMS.appState] = FILTER_URL_UNSPECIFIED_STATE;
    if (state.modelUnspecified) params[FILTER_URL_PARAMS.modelState] = FILTER_URL_UNSPECIFIED_STATE;
    if (state.providerUnspecified) params[FILTER_URL_PARAMS.providerState] = FILTER_URL_UNSPECIFIED_STATE;
    if (state.user !== null) params[FILTER_URL_PARAMS.user] = state.user;
    if (state.status.length > 0) {
      params[FILTER_URL_PARAMS.status] = state.status.join(FILTER_URL_STATUS_SEPARATOR);
    }

    return params;
  }

  // ─── Re-assert: after navigation (R-008 addition, 2026-08-26) ─────────────

  private trackNavigationState(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) this.navigating = true;
      if (
        event instanceof NavigationEnd
        || event instanceof NavigationCancel
        || event instanceof NavigationError
      ) {
        this.navigating = false;
      }
    });
  }

  private reassertAfterNavigation(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const expected = this.currentParams();
        // Idempotent: a URL that already carries the correct parameters
        // triggers no second write, so the bar's own writes cannot re-trigger
        // this and Back still walks views only.
        if (this.matchesUrl(expected)) return;
        this.write(expected);
      });
  }

  private matchesUrl(expected: Params): boolean {
    const actual = this.router.parseUrl(this.router.url).queryParams;
    return Object.entries(expected).every(([name, value]) => {
      const present = actual[name];
      return value === null ? present === undefined : present === value;
    });
  }

  private deepestRoute(): ActivatedRoute {
    let route = this.route.root;
    while (route.firstChild) route = route.firstChild;
    return route;
  }
}

/** An empty or whitespace-only parameter value is an absent parameter. */
function blankAsNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * The published blank rule (GH-164 C-1, `DimensionClassification.IsUnspecified`): empty or ASCII
 * whitespace only is absent; anything else is returned VERBATIM, never trimmed.
 */
function asciiBlankAsNull(value: string): string | null {
  return /^[\t\n\v\f\r ]*$/.test(value) ? null : value;
}
